import { ResizeIcon } from "@/overlay/components/Legends/ResizeIcon";
import { classNames } from "@/utility/jsx";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import styles from "./ResizeHandle.module.scss";

export type ResizeHandleProps = {
  onDrag: (height: number) => void;
  onDragEnd: () => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  isHidden: boolean;
};

export const ResizeHandle = ({ containerRef, isHidden, onDrag, onDragEnd }: ResizeHandleProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRectRef = useRef<DOMRect | null>(null);
  const onDragRef = useRef(onDrag);
  const onDragEndRef = useRef(onDragEnd);

  useEffect(() => {
    onDragRef.current = onDrag;
  }, [onDrag]);

  useEffect(() => {
    onDragEndRef.current = onDragEnd;
  }, [onDragEnd]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) {
      return;
    }

    const containerRect = containerRef?.current?.getBoundingClientRect();
    if (!containerRect) {
      return;
    }

    dragStartRectRef.current = containerRect;
    setIsDragging(true);
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const containerRect = dragStartRectRef.current;
      if (!containerRect) {
        return;
      }

      const deltaX = e.clientX - containerRect.left;
      const deltaY = containerRect.bottom - e.clientY;

      const delta = Math.max(deltaX - containerRect.width, deltaY - containerRect.height);
      const newHeight = containerRect.height + delta;

      onDragRef.current(newHeight);
    };

    const stopDragging = () => {
      if (!dragStartRectRef.current) {
        return;
      }

      dragStartRectRef.current = null;
      setIsDragging(false);
      onDragEndRef.current();
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("blur", stopDragging);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, [isDragging]);

  return (
    <div
      className={classNames(styles["resize-handle"], isDragging && styles["resize-handle--dragging"])}
      style={{
        display: isHidden ? "none" : "",
      }}
      onMouseDown={handleMouseDown}
      data-testid="resize-handle"
    >
      <ResizeIcon />
    </div>
  );
};
